
import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Spin } from "antd";
import { GoogleOAuthProvider } from '@react-oauth/google';

// --- Service and Redux Imports ---
import { fetchUserProfile } from "./services/user.profile";
import { ProfileRedu, setSubscription } from "./store/Slices/ProfileSlice";
import { ChangeSinceDate } from "./store/Slices/DateSlice";
import { ChangeUntilDate } from "./store/Slices/UntilDateSlice";
import {
fetchGroups,
setSelectedGroup,
} from "./store/Slices/GroupSlice";

// --- Component Imports ---
import Signup from "./Components/Signup";
import Onboarding from "./Components/Onboarding";
import Login from "./Components/Login";
import Dashboard from "./Components/Dashboard";
import ForgotPassword from "./Components/ForgotPassword";
import ResetPassword from "./Components/ResetPassword";
import ForgotPasswordConfirmation from "./Components/ForgotPasswordConfirmation";
import BuySubscription from "./Components/BuySubscription";
import VerifyOtp from "./Components/VerifyOtp";
import Unsubscribe from "./Components/Unsubscribe";
import UnsubscribeSuccess from "./Components/UnsubscribeSuccess";
import UnsubscribeError from "./Components/UnsubscribeError";

// --- Redirect Page Imports ---
import Facebook from "./Components/Redirectpage/Facebook";
import Linkedin from "./Components/Redirectpage/Linkedin";
import Youtube from "./Components/Redirectpage/Youtube";
import Instagram from "./Components/Redirectpage/Instagram";
import Pinterest from "./Components/Redirectpage/pinterest";
import Twitter from "./Components/Redirectpage/Twitter";
import GMBRedirect from "./Components/Redirectpage/GMBRedirect";
import TiktokRedirect from "./Components/Redirectpage/TiktokRedirect";
import SharedLinkView from "./Components/Public/SharedLinkView";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const POST_AUTH_REDIRECT_KEY = "post_auth_redirect";
const AUTH_SOURCE_KEY = "auth_source";

const isSafeInternalRedirect = (value) => {
if (!value) return false;
return value.startsWith("/") && !value.startsWith("//");
};

const getRedirectFromSearch = (search) => {
const params = new URLSearchParams(search);
const source = params.get("source");
const redirect = params.get("redirect");

if (source === "mini-tools" && isSafeInternalRedirect(redirect)) {
return {
source,
redirect,
};
}

return null;
};

const capturePostAuthRedirectFromSearch = (search) => {
const redirectData = getRedirectFromSearch(search);

if (redirectData) {
sessionStorage.setItem(AUTH_SOURCE_KEY, redirectData.source);
sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, redirectData.redirect);
return redirectData.redirect;
}

return null;
};

const getPostAuthRedirect = (fallback = "/Dashboard") => {
const redirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
return isSafeInternalRedirect(redirect) ? redirect : fallback;
};

function App() {
const dispatch = useDispatch();
const token = localStorage.getItem("user_Token") || sessionStorage.getItem("user_Token");

const [isAppLoading, setIsAppLoading] = useState(true);

const userProfile = useSelector(state => state.Profiles[0]);
const { subscription } = useSelector(state => state.subscription);

useEffect(() => {
capturePostAuthRedirectFromSearch(window.location.search);
}, []);

useEffect(() => {
const initializeApp = async () => {
const d = new Date();
const month = (d.getMonth() + 1).toString().padStart(2, "0");
const day = d.getDate().toString().padStart(2, "0");
dispatch(ChangeSinceDate(Date.parse(`${d.getFullYear()}/${month}/01`) / 1000));
dispatch(ChangeUntilDate(Date.parse(`${d.getFullYear()}/${month}/${day}`) / 1000));

if (token) {
try {
const { user, subscription } = await fetchUserProfile();
dispatch(ProfileRedu(user));
dispatch(setSubscription(subscription));

if (subscription?.status === "active") {
const groupsAction = await dispatch(fetchGroups());
const fetchedGroups = Array.isArray(groupsAction.payload)
? groupsAction.payload
: [];
const storedGroup = localStorage.getItem("selected_group");
const activeGroups = fetchedGroups.filter(
(group) => group.status !== "pending"
);

let resolvedGroupName = null;

if (
storedGroup &&
activeGroups.some((group) => group.name === storedGroup)
) {
resolvedGroupName = storedGroup;
} else if (activeGroups.length > 0) {
resolvedGroupName = activeGroups[0].name;
}

await dispatch(setSelectedGroup(resolvedGroupName));
}

} catch (error) {
console.error("Initialization error, clearing auth.", error);
localStorage.clear();
sessionStorage.clear();
}
}
setIsAppLoading(false);
};

initializeApp();
}, [dispatch, token]);

const ProtectedRoute = ({ children }) => {
if (!token) {
return <Navigate to="/login" replace />;
}
return children;
};

const SubscriptionProtectedRoute = ({ children }) => {
if (!token) {
return <Navigate to="/login" replace />;
}

if (subscription?.status !== "active") {
return <Navigate to="/select-plan" replace />;
}

return children;
};

if (isAppLoading) {
return (
<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
<Spin size="large" />
</div>
);
}

return (
<div className="App">
<ToastContainer
position="top-right"
autoClose={5000}
className="oolook-toast-container"
/>
<Routes>
{/* Public routes */}
<Route
path="/login"
element={
token ? (
<Navigate to="/" replace />
) : (
<Login />
)
}
/>
<Route
path="/Signup"
element={
token ? (
<Navigate to="/" replace />
) : (
<Signup />
)
}
/>
<Route
path="/complete-google-signup"
element={<Signup googleCompletionMode />}
/>
<Route path="/verify-otp" element={<VerifyOtp />} />
<Route path="/forgot-password" element={<ForgotPassword />} />
<Route path="/reset-password/:token" element={<ResetPassword />} />
<Route path="/forgot-password-confirmation" element={<ForgotPasswordConfirmation />} />
<Route path="/unsubscribe" element={<Unsubscribe />} />
<Route path="/unsubscribed-successfully" element={<UnsubscribeSuccess />} />
<Route path="/unsubscribe-error" element={<UnsubscribeError />} />
<Route path="/s/:shareableId" element={<SharedLinkView />} />
{/* Protected routes */}
<Route path="/Dashboard/*" element={<SubscriptionProtectedRoute><Dashboard /></SubscriptionProtectedRoute>} />
<Route path="/Onboarding" element={<SubscriptionProtectedRoute><Onboarding /></SubscriptionProtectedRoute>} />
<Route path="/select-plan" element={<ProtectedRoute><BuySubscription /></ProtectedRoute>} />
{/* Social Media Redirect Handlers */}
<Route path="/FBRedirect" element={<Facebook />} />
<Route path="/LinkRedirect" element={<Linkedin />} />
<Route path="/YTRedirect" element={<Youtube />} />
<Route path="/instaRedirect" element={<Instagram />} />
<Route path="/PintRedirect" element={<Pinterest />} />
<Route path="/twitterRedirect" element={<Twitter />} />
<Route path="/GMBRedirect" element={<GMBRedirect />} />
<Route path="/TiktokRedirect" element={<TiktokRedirect />} />
{/* Root path navigation logic */}
<Route
path="/"
element={
!token ? (
<Navigate to="/login" replace />
) : subscription?.status === 'active' ? (
<Navigate to={getPostAuthRedirect("/Dashboard")} replace />
) : (
<Navigate to="/select-plan" replace />
)
}
/>
{/* Catch-all for any other route */}
<Route path="*" element={<Navigate to="/" replace />} />
</Routes>
</div>
);
}

const Root = () => (
<BrowserRouter>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <App />
    </GoogleOAuthProvider>
</BrowserRouter>
);

export default Root;
